import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { LeadSource } from "../../../../../../../models/leadsource";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import LeadSourceModal from "./LeadSourceModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const LeadSourcePage = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const {
    page,
    pageSize,
    hasErrors,
    total,
    leadSources,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createLeadSource,
    updateLeadSource,
  } = useActions();

  const [selectedLeadSource, setSelectedLeadSource] = useState<LeadSource>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status.toLowerCase() === Status.ACTIVE.toLowerCase() ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const editLeadSource = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const findLeadSource = leadSources.find(
      (leadSource) => Number(leadSource.id) === Number(index)
    );

    if (findLeadSource) {
      setSelectedLeadSource(findLeadSource);
      setOpenModal(true);
    }
  };

  const changeStatusLeadSource = (index: number) => {
    const findLeadSource = leadSources.find(
      (leadSource) => Number(leadSource.id) === Number(index)
    );

    if (findLeadSource) {
      updateLeadSource({
        id: Number(index),
        leadSource: {
          ...findLeadSource,
          status: findLeadSource.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeStatusButton = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeButtonStatus}
          data-cy="changeStatus"
          customColor={Color.INFO}
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveLeadSourceSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, leadSources);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    {
      name: "code",
      title: translate("code"),
    },
    {
      name: "name",
      title: translate("name"),
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission.create)
      ? {
        text: translate("create"),
        action: () => {
          if (hasErrors !== undefined) setHasErrors(undefined);
          setSelectedLeadSource(undefined);
          setOpenModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        {
          name: translate("code"),
          column: "code",
        },
        {
          name: translate("leadSource"),
          column: "name",
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderChangeStatusButton(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      setHasErrors(undefined);
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.lead_source",
      });
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="leadSourceDataList">
      <ShipeeziNewGrid
        data={leadSources}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (leadSourceId) => editLeadSource(Number(leadSourceId))
            : undefined,
          handleChange: (leadSourceId) => changeStatusLeadSource(Number(leadSourceId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveLeadSourceSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <LeadSourceModal
          leadSource={selectedLeadSource}
          open={openModal}
          hasError={hasErrors}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          updateOrCreateLeadSource={(leadSource: LeadSource) =>
            selectedLeadSource
              ? selectedLeadSource.id &&
                updateLeadSource({
                  id: selectedLeadSource.id,
                  leadSource: { ...selectedLeadSource, ...leadSource },
                })
              : createLeadSource({
                  ...leadSource,
                  companyId: currentUser?.companyId,
                  valid: true,
                })
          }
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateLeadSource({
                id: rowId,
                leadSource: {
                  status,
                } as LeadSource,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default LeadSourcePage;
